import { useMemo, useState, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Search, Copy, Check, ChevronDown, ChevronRight,
  FileText, Loader2, Lock, ExternalLink, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { adminApi } from '@/services/api/admin'
import type { PromptSummary, PromptDetail, PromptVariant } from '@/services/api/admin'
import { cn } from '@/lib/utils'

type CategoryKey = 'evaluation' | 'enhancement'

const CATEGORY_LABEL: Record<CategoryKey, string> = {
  evaluation: 'Evaluation',
  enhancement: 'Enhancement',
}

const SUBCATEGORY_ORDER: Record<CategoryKey, string[]> = {
  evaluation: ['Readability', 'Relevance', 'Checklists & Specialized', 'Reranking'],
  enhancement: ['Content Generation', 'Formatting', 'Summaries & Metadata'],
}

interface Grouped {
  category: CategoryKey
  total: number
  subgroups: { subcategory: string; prompts: PromptSummary[] }[]
}

function groupPrompts(prompts: PromptSummary[], search: string): Grouped[] {
  const needle = search.trim().toLowerCase()
  const match = (p: PromptSummary) =>
    !needle ||
    p.name.toLowerCase().includes(needle) ||
    p.purpose.toLowerCase().includes(needle) ||
    p.subcategory.toLowerCase().includes(needle)

  const filtered = prompts.filter(match)
  const out: Grouped[] = []

  for (const category of ['evaluation', 'enhancement'] as CategoryKey[]) {
    const inCat = filtered.filter(p => p.category === category)
    if (!inCat.length) continue
    const subgroups: { subcategory: string; prompts: PromptSummary[] }[] = []
    const order = SUBCATEGORY_ORDER[category]
    const seen = new Set<string>()
    for (const sub of order) {
      const list = inCat.filter(p => p.subcategory === sub)
      if (list.length) {
        subgroups.push({ subcategory: sub, prompts: list })
        seen.add(sub)
      }
    }
    // Any subcategory not in the ordered list (defensive)
    for (const p of inCat) {
      if (!seen.has(p.subcategory)) {
        subgroups.push({ subcategory: p.subcategory, prompts: [p] })
        seen.add(p.subcategory)
      }
    }
    out.push({ category, total: inCat.length, subgroups })
  }
  return out
}

// Split a template into a sequence of text and {variable} chip tokens.
function tokenizeTemplate(template: string): Array<{ type: 'text' | 'var'; value: string }> {
  // Match {identifier} or {identifier.something}, but skip JSON examples that use {{ }}.
  // Strategy: temporarily replace {{ and }} so they don't confuse single-brace detection.
  const tokens: Array<{ type: 'text' | 'var'; value: string }> = []
  const placeholder = ' '
  const safe = template.replace(/\{\{/g, placeholder + 'L').replace(/\}\}/g, placeholder + 'R')
  const pattern = /\{([a-zA-Z_][a-zA-Z0-9_.[\]'"\s:]{0,80})\}/g
  let lastIdx = 0
  let m: RegExpExecArray | null
  while ((m = pattern.exec(safe)) !== null) {
    if (m.index > lastIdx) {
      tokens.push({
        type: 'text',
        value: safe.slice(lastIdx, m.index).replace(new RegExp(placeholder + 'L', 'g'), '{').replace(new RegExp(placeholder + 'R', 'g'), '}'),
      })
    }
    tokens.push({ type: 'var', value: m[1] })
    lastIdx = m.index + m[0].length
  }
  if (lastIdx < safe.length) {
    tokens.push({
      type: 'text',
      value: safe.slice(lastIdx).replace(new RegExp(placeholder + 'L', 'g'), '{').replace(new RegExp(placeholder + 'R', 'g'), '}'),
    })
  }
  return tokens
}

function CategoryHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="px-3 pt-4 pb-1 text-[11px] font-bold tracking-wider text-muted-foreground uppercase">
      {label} <span className="ml-1 font-normal opacity-70">({count})</span>
    </div>
  )
}

function SubgroupSection({
  subcategory,
  prompts,
  selectedId,
  onSelect,
}: {
  subcategory: string
  prompts: PromptSummary[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(true)
  return (
    <div className="px-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-foreground/80 hover:bg-muted/50 rounded"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span>{subcategory}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">{prompts.length}</span>
      </button>
      {open && (
        <ul className="ml-4 mt-0.5 mb-1 border-l border-border/60">
          {prompts.map(p => {
            const selected = p.id === selectedId
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelect(p.id)}
                  className={cn(
                    'w-full text-left px-2.5 py-1.5 text-xs rounded-r transition-colors',
                    selected
                      ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary -ml-px'
                      : 'text-foreground/80 hover:bg-muted/50',
                  )}
                  title={p.purpose}
                >
                  {p.name}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function VariableChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono bg-blue-100 text-blue-800 border border-blue-200 whitespace-nowrap mx-0.5">
      {name}
    </span>
  )
}

function TemplateBody({ template }: { template: string }) {
  const tokens = useMemo(() => tokenizeTemplate(template), [template])
  return (
    <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed font-mono text-foreground/90 p-4 bg-muted/40 rounded-md border max-h-[60vh] overflow-y-auto">
      {tokens.map((t, i) =>
        t.type === 'text'
          ? <Fragment key={i}>{t.value}</Fragment>
          : <VariableChip key={i} name={t.value} />,
      )}
    </pre>
  )
}

function VariantPanel({ variants }: { variants: PromptVariant[] }) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(variants[0]?.article_type || '')
  if (!variants.length) return null
  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/50"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Article-type variants
        <span className="text-xs text-muted-foreground">
          {variants.length} types: {variants.map(v => v.article_type).join(' / ')}
        </span>
      </button>
      {open && (
        <div className="border-t p-3 space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {variants.map(v => (
              <button
                key={v.article_type}
                type="button"
                onClick={() => setActive(v.article_type)}
                className={cn(
                  'px-2.5 py-1 text-xs rounded border transition-colors',
                  active === v.article_type
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background hover:bg-muted/50 border-border',
                )}
              >
                {v.article_type}
              </button>
            ))}
          </div>
          {variants.filter(v => v.article_type === active).map(v => (
            <pre
              key={v.article_type}
              className="whitespace-pre-wrap text-xs font-mono p-3 bg-muted/40 rounded border max-h-72 overflow-y-auto"
            >
              {v.body}
            </pre>
          ))}
        </div>
      )}
    </div>
  )
}

function DetailPane({ detail }: { detail: PromptDetail }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(detail.template)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Browser may block writeText in some contexts — silently ignore.
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold">{detail.name}</h2>
            <div className="text-xs text-muted-foreground mt-0.5">
              {CATEGORY_LABEL[detail.category]} · {detail.subcategory}
            </div>
          </div>
          <Badge variant="outline" className="gap-1 text-[11px]">
            <Lock className="h-3 w-3" /> Read-only
          </Badge>
        </div>
        <p className="text-sm text-foreground/90">{detail.purpose}</p>
      </div>

      {/* Used in */}
      <div className="text-sm">
        <span className="text-muted-foreground">Used in: </span>
        <span className="font-medium">{detail.used_in}</span>
      </div>

      {/* Inputs */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Inputs
        </div>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr className="border-b">
                <th className="text-left px-3 py-2 font-medium w-1/4">Variable</th>
                <th className="text-left px-3 py-2 font-medium w-1/5">Type</th>
                <th className="text-left px-3 py-2 font-medium">Description</th>
              </tr>
            </thead>
            <tbody>
              {detail.inputs.map((inp, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-mono text-blue-800">{inp.name}</td>
                  <td className="px-3 py-2 font-mono text-muted-foreground">{inp.type || '—'}</td>
                  <td className="px-3 py-2 text-foreground/80">{inp.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Template */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Prompt template
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!detail.template}
            className="h-7 text-xs gap-1.5"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy template'}
          </Button>
        </div>
        {detail.template ? (
          <TemplateBody template={detail.template} />
        ) : (
          <div className="text-xs text-muted-foreground italic p-4 border rounded-md bg-muted/30">
            Template could not be extracted from source. Open the source file directly for details.
          </div>
        )}
      </div>

      {/* Variants */}
      {detail.variants.length > 0 && <VariantPanel variants={detail.variants} />}

      {/* Notes */}
      {detail.notes.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-center gap-1.5 font-medium mb-1.5">
            <Info className="h-3.5 w-3.5" /> Notes
          </div>
          <ul className="list-disc list-inside space-y-1">
            {detail.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* Source */}
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t">
        <ExternalLink className="h-3 w-3" />
        Source: <code className="font-mono">{detail.source_path}</code>
      </div>
    </div>
  )
}

export function PromptsTab() {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['adminPrompts'],
    queryFn: () => adminApi.getPrompts(),
  })

  const prompts = listData?.prompts ?? []
  const grouped = useMemo(() => groupPrompts(prompts, search), [prompts, search])

  // Default-select the first prompt once loaded
  const effectiveSelectedId = selectedId || prompts[0]?.id || null

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['adminPromptDetail', effectiveSelectedId],
    queryFn: () => adminApi.getPrompt(effectiveSelectedId!),
    enabled: !!effectiveSelectedId,
  })

  return (
    <Card>
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[600px]">
          {/* Left rail */}
          <div className="border-r bg-muted/20">
            <div className="p-3 border-b">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">AI Prompts</span>
                <span className="text-xs text-muted-foreground ml-auto">{prompts.length}</span>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search prompts..."
                  className="h-8 pl-7 text-sm"
                />
              </div>
            </div>

            <div className="py-1 max-h-[680px] overflow-y-auto">
              {listLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : grouped.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-8">
                  No prompts match "{search}"
                </div>
              ) : (
                grouped.map(g => (
                  <div key={g.category}>
                    <CategoryHeader label={CATEGORY_LABEL[g.category].toUpperCase()} count={g.total} />
                    {g.subgroups.map(sg => (
                      <SubgroupSection
                        key={sg.subcategory}
                        subcategory={sg.subcategory}
                        prompts={sg.prompts}
                        selectedId={effectiveSelectedId}
                        onSelect={setSelectedId}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right detail pane */}
          <div className="p-6">
            {!effectiveSelectedId ? (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Select a prompt to view its details
              </div>
            ) : detailLoading || !detail ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <DetailPane detail={detail} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default PromptsTab
